import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  AdaptTableComponent,
  ColumnConfig,
  DataCellTemplateParams,
  DataCellTooltipParams,
  ExportType,
  ImplicitTemplateParams,
  RowData,
  ToolbarConfig,
  ToolbarItemContext
} from '@bmc-ux/adapt-table';
import {
  COLUMN_HEADER,
  SavedAdvancedFilterEvent,
  TABLE_DATE_TYPE,
  TABLE_SELECTION_MODE,
  TableDate,
  TableDateTime,
  TableFilterUrlParams,
  TableSavedAdvancedFilter
} from '../../../models/table';
import {TableExportService} from '../../../services/table-export.service';
import {GlobalTableFilterService} from '../../../services/global-table-filter.service';
import {BadgeClasses, Language, PRODUCT_SUB_TYPE, ProductInfo, ProductStatus, PublishingStatus} from '../../../models/product';
import {COLUMN_CONFIG, TABLE_TYPE} from '../../../models/column-config';
import {OperatingSystem} from '../../../models/operating-system';
import {CmpType, OS_CMP_CATEGORY, OS_CMP_CATEGORY_TITLE, OsComponent} from '../../../models/os-component';
import {environment} from '../../../../environments/environment';
import {ReportProduct} from '../../../report-module/report';
import {ActivatedRoute, Router} from '@angular/router';
import {
  AdaptFilterControlsComponent,
  AdaptRxDatetimeAdapter,
  AdvancedFilterOption,
  AdvancedFilterOptionDataType,
  AdvancedFilterSelectionTypeData,
  FilterOptionTemplateParams,
  SavedAdvancedFilter,
  SelectedAdvancedFilter, SimpleFilterOption
} from '@bmc-ux/adapt-angular';
import {GlobalAdvancedTableFilterService} from '../../../services/global-advanced-table-filter.service';
import {
  BeforeActiveSavedFilterChangeEvent,
  CreateAdvancedFilterEvent,
  UpdateSavedFilterEvent
} from '@bmc-ux/adapt-angular/advanced-filtering/advanced-filter/advanced-filter.models';
import {RxSelectOption} from '@bmc-ux/adapt-angular/rx';
import {ColumnHeaderTemplateParams, ExportEvent, RowDataItem} from '@bmc-ux/adapt-table/lib/adapt-table.models';
import {USER_PREFERENCE_KEY, UserPreference} from '../../../models/user';
import {
  FEEDBACK_COLUMN_CONFIG,
  FEEDBACK_TABLE_TYPE,
  FEEDBACK_SIMPLE_FILTER_OPTIONS,
  FEEDBACK_SIMPLE_FILTER_KEY,
  SimpleFilterKey2FilterOptions
} from '../../../models/feedback-table-config';
import {FeedbackModel, FEEDBACK_FORM_TYPE} from '../../../models/feedback';
import * as moment from 'moment';
import {AdaptRxDatetimeMomentAdapter} from '../../../models/AdaptRxDatetimeMomentAdapter';
import {FilterValue} from '@bmc-ux/adapt-angular/advanced-filtering/advanced-filter/filter-controls/filter-controls.component';
import {ProductHistory} from 'src/app/models/history';
import {GlobalSimpleTableFilterService} from '../../../services/global-simple-table-filter.service';
import {EmailModel, EMAIL_COLUMN_CONFIG, EMAIL_IS_ADDRESSED, EMAIL_SIMPLE_FILTER_KEY, EMAIL_SIMPLE_FILTER_OPTIONS} from 'src/app/models/email.model';


interface RowDataChanges extends SimpleChange {
  currentValue: any[];
  previousValue: any[];
}

interface MainPrimeNgTableComponentChanges extends SimpleChanges {
  rowData: RowDataChanges;
}

@Component({
  selector: 'app-main-primeng-table',
  templateUrl: './main-primeng-table.component.html',
  styleUrls: ['./main-primeng-table.component.sass'],
  providers: [{provide: AdaptRxDatetimeAdapter, useClass: AdaptRxDatetimeMomentAdapter}]
})
export class MainPrimeNgTableComponent implements OnInit, OnChanges {
  @Input() public id: string;
  @Input() public bordered = true;
  @Input() public resizableColumns = true;
  @Input() public reorderableColumns = false;
  @Input() public scrollable = false;
  @Input() public scrollHeight = '350px';
  @Input() public virtualScroll = false;
  @Input() public virtualRowHeight;
  @Input() public sortable = true;
  @Input() public filterable = false;
  @Input() public visibleColumnsMenu = false;
  @Input() public isToolbar = true;
  @Input() public searchButton = false;
  @Input() public showCurrentPageReport = true;
  @Input() public expandedRowTemplate;
  @Input() public columnResizeMode;
  @Input() public wrapCellText = false;
  @Input() public tooltipInitialDelayMs = 500;

  @Input() public first = 0;
  @Input() public rows = 15;

  @Input() public dataKey: string;
  @Input() public loading = true;

  @Input() public userPreferences: UserPreference[];
  @Input() public rowData: any[] = [];
  @Input() public columnConfig: ColumnConfig[] = [];
  @Input() public actionsColumnConfig: ColumnConfig[] = [];
  @Input() public tableFilterUrlParams: TableFilterUrlParams = null;
  @Input() public category: OS_CMP_CATEGORY.DBMS | OS_CMP_CATEGORY.MT | OS_CMP_CATEGORY.TPC | OS_CMP_CATEGORY.TPC_PREREQ | OS_CMP_CATEGORY.UI_LANG | OS_CMP_CATEGORY.DOCS_LANG;
  @Input() public tableType: TABLE_TYPE.COMPONENTS |
    TABLE_TYPE.PRODUCTS |
    TABLE_TYPE.OPERATING_SYSTEMS |
    TABLE_TYPE.LANGUAGES |
    TABLE_TYPE.TECHNOLOGIES |
    TABLE_TYPE.MASTER_PRODUCTS |
    TABLE_TYPE.MASTER_COMPONENTS |
    TABLE_TYPE.MASTER_OPERATING_SYSTEMS |
    TABLE_TYPE.MASTER_TECHNOLOGIES |
    TABLE_TYPE.MASTER_LANGUAGES |
    TABLE_TYPE.MASTER_TYPES |
    TABLE_TYPE.REPORT_PRODUCTS |
    TABLE_TYPE.PRODUCT_VERSIONS |
    TABLE_TYPE.HISTORY |
    TABLE_TYPE.EMAIL_REPORT |
    FEEDBACK_TABLE_TYPE.BY_SN_CLIENT |
    FEEDBACK_TABLE_TYPE.ALL |
    FEEDBACK_TABLE_TYPE.ALL_CLIENT |
    FEEDBACK_TABLE_TYPE.ALL_ADMIN;

  @Input() public currentProduct: ProductInfo = null;

  @Input() public selectionMode: TABLE_SELECTION_MODE = null;
  @Input() public selection: any[] = null;

  @Input() public disabledRowSelectionResolver: (rowData: RowData) => boolean;

  @Output() public selectionChange: EventEmitter<any> = new EventEmitter();
  @Output() public handleSavedAdvancedFilter: EventEmitter<SavedAdvancedFilterEvent> = new EventEmitter<SavedAdvancedFilterEvent>();
  @Output() public handleVisibilityColumns: EventEmitter<string> = new EventEmitter<string>();
  @Output() public viewEmail: EventEmitter<EmailModel> = new EventEmitter<EmailModel>();
  @Output() public viewHistoryLog: EventEmitter<EmailModel> = new EventEmitter<EmailModel>();

  @ViewChild('adaptTable', {static: false}) public adaptTable: AdaptTableComponent;
  @ViewChild('tableCellTemplate', {static: true}) public tableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('linkCellTemplate', {static: true}) public linkCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('thumbUpHeaderTitleTemplate', {static: true}) public thumbUpHeaderTitleTemplate: TemplateRef<ColumnHeaderTemplateParams>;
  @ViewChild('thumbDownHeaderTitleTemplate', {static: true}) public thumbDownHeaderTitleTemplate: TemplateRef<ColumnHeaderTemplateParams>;
  @ViewChild('toolbarTableFilterTemplate', {static: true}) public toolbarTableFilterTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;
  @ViewChild('advancedFilterTemplate', {static: true}) public advancedFilterTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;
  @ViewChild('feedbackFilterTemplate', {static: true}) public feedbackFilterTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;

  @ViewChild('inputsForReleaseDateTemplate', {static: true}) public inputsForReleaseDateTemplate: TemplateRef<FilterOptionTemplateParams>;
  @ViewChild('leftCustomSectionTemplate', {static: true}) public leftCustomSectionTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;
  @ViewChild('productStatusCellTemplate', {static: true}) public productStatusCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('publishingStatusCellTemplate', {static: true}) public publishingStatusCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('publishedVersionCellTemplate', {static: true}) public publishedVersionCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('dbNotesTableCellTemplate', {static: true}) public dbNotesTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('osNotesTableCellTemplate', {static: true}) public osNotesTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('osDetailsTableCellTemplate', {static: true}) public osDetailsTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('techTypeTableCellTemplate', {static: true}) public techTypeTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('masterTechTypeTableCellTemplate', {static: true}) public masterTechTypeTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('dropdownCmpTypeTableCellTemplate', {static: true}) public dropdownCmpTypeTableCellTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('visibleColumnsCustomTemplate', {static: true}) public visibleColumnsCustomTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;
  @ViewChild('dateTemplate', {static: true}) public dateTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('dateTimeTemplate', {static: true}) public dateTimeTemplate: TemplateRef<DataCellTemplateParams>;

  @ViewChild('feedbackTypeTemplate', {static: true}) public feedbackTypeTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('emailTemplate', {static: true}) public emailTemplate: TemplateRef<DataCellTemplateParams>;

  @ViewChild('voteTimeTemplate', {static: true}) public voteTimeTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('pageLinkTemplate', {static: true}) public pageLinkTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('descrTemplate', {static: true}) public descrTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('smartNumberTemplate', {static: true}) public smartNumberTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('viewEmailButtonTemplate', {static: true}) public viewEmailButtonTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('viewLogButtonTemplate', {static: true}) public viewLogButtonTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('emailsIsAddressedTemplate', {static: true}) public emailsIsAddressedTemplate: TemplateRef<DataCellTemplateParams>;
  @ViewChild('emailManagementFilterTemplate', {static: true}) public emailManagementFilterTemplate: TemplateRef<ImplicitTemplateParams<ToolbarItemContext>>;


  public readonly host = window.location.origin;
  public readonly ENV = environment;
  public readonly badgeClasses: typeof BadgeClasses = BadgeClasses;
  public readonly NESTED_OS_TECHS = OS_CMP_CATEGORY;
  public readonly NESTED_OS_TECHS_TITLE = OS_CMP_CATEGORY_TITLE;
  public readonly COLUMN_HEADER = COLUMN_HEADER;
  public readonly FEEDBACK_TABLE_TYPE = FEEDBACK_TABLE_TYPE;
  public readonly FEEDBACK_SIMPLE_FILTER_OPTIONS: typeof FEEDBACK_SIMPLE_FILTER_OPTIONS = FEEDBACK_SIMPLE_FILTER_OPTIONS;
  public readonly FEEDBACK_SIMPLE_FILTER_KEY: typeof FEEDBACK_SIMPLE_FILTER_KEY = FEEDBACK_SIMPLE_FILTER_KEY;
  public readonly FEEDBACK_FORM_TYPE: typeof FEEDBACK_FORM_TYPE = FEEDBACK_FORM_TYPE;
  public readonly EMAIL_IS_ADDRESSED =  EMAIL_IS_ADDRESSED;
  public readonly EMAIL_SIMPLE_FILTER_KEY: typeof EMAIL_SIMPLE_FILTER_KEY = EMAIL_SIMPLE_FILTER_KEY;
  public readonly EMAIL_SIMPLE_FILTER_OPTIONS: typeof EMAIL_SIMPLE_FILTER_OPTIONS = EMAIL_SIMPLE_FILTER_OPTIONS;

  public savedFilters: TableSavedAdvancedFilter[] = [];
  public selectedAdvancedFilters: SelectedAdvancedFilter[] = [];
  public selectedFeedbackSimpleFilters: SimpleFilterKey2FilterOptions = {};

  public activeSavedFilter: TableSavedAdvancedFilter = null;
  public filterText: string;
  public filteredRowData: any[] = [];
  public toolbarConfig: ToolbarConfig = {
    counter: true,
    filter: true,
    filterResults: true,
    rightCustomSection: true
  };

  public pastDaysPresetModel: number;
  public sharedSavedAdvancedFilters: TableSavedAdvancedFilter[] = [];
  public advancedFilterOptions: AdvancedFilterOption[] = [
    {
      id: 'publishingStatus',
      label: COLUMN_HEADER.PUBLISHING_STATUS,
      dataType: AdvancedFilterOptionDataType.selection,
      data: {
        selectOptions: [
          {id: PublishingStatus.PUBLISHED, name: PublishingStatus.PUBLISHED},
          {id: PublishingStatus.NEW, name: PublishingStatus.NEW},
          {id: PublishingStatus.REVIEW, name: PublishingStatus.REVIEW},
          {id: PublishingStatus.DRAFT, name: PublishingStatus.DRAFT},
          {id: PublishingStatus.WITHDRAWN, name: PublishingStatus.WITHDRAWN}
        ],
      } as AdvancedFilterSelectionTypeData,
    },
    {
      id: 'hasPublishedVersion',
      label: COLUMN_HEADER.PUBLISHED_DATA,
      dataType: AdvancedFilterOptionDataType.boolean,
    },
    {
      id: 'productFillInTabsCount',
      label: COLUMN_HEADER.TABS,
      dataType: AdvancedFilterOptionDataType.selection,
      data: {
        selectOptions: [
          {id: '0', name: '0'},
          {id: '1', name: '1'},
          {id: '2', name: '2'},
          {id: '3', name: '3'},
          {id: '4', name: '4'}
        ],
      } as AdvancedFilterSelectionTypeData,
    },
    {
      id: 'productStatus',
      label: COLUMN_HEADER.PRODUCT_STATUS,
      dataType: AdvancedFilterOptionDataType.selection,
      data: {
        selectOptions: [
          {id: ProductStatus.AVAILABLE, name: ProductStatus.AVAILABLE},
          {id: ProductStatus.BETA, name: ProductStatus.BETA},
          {id: ProductStatus.DEVELOPMENT, name: ProductStatus.DEVELOPMENT},
          {id: ProductStatus.CANCELED, name: ProductStatus.CANCELED},
          {id: ProductStatus.GA, name: ProductStatus.GA},
          {id: ProductStatus.OBSOLETE, name: ProductStatus.OBSOLETE},
          {id: ProductStatus.PLANNING, name: ProductStatus.PLANNING},
          {id: ProductStatus.SUPPORT_ONLY, name: ProductStatus.SUPPORT_ONLY},
          {id: ProductStatus.WIP, name: ProductStatus.WIP},
          {id: ProductStatus.WITHDRAWN, name: ProductStatus.WITHDRAWN},
          {id: ProductStatus.TRANSITION, name: ProductStatus.TRANSITION},
        ],
      } as AdvancedFilterSelectionTypeData,
    },
    {
      id: 'productSubType',
      label: COLUMN_HEADER.SUB_TYPE,
      dataType: AdvancedFilterOptionDataType.selection,
      data: {
        selectOptions: [
          {id: PRODUCT_SUB_TYPE.DR___3PCOMMERCIAL, name: PRODUCT_SUB_TYPE.DR___3PCOMMERCIAL},
          {id: PRODUCT_SUB_TYPE.DR___3PMKTRESALE, name: PRODUCT_SUB_TYPE.DR___3PMKTRESALE},
          {id: PRODUCT_SUB_TYPE.DR___3PROYALTEMBED, name: PRODUCT_SUB_TYPE.DR___3PROYALTEMBED},
          {id: PRODUCT_SUB_TYPE.DR___3PROYALFREETE, name: PRODUCT_SUB_TYPE.DR___3PROYALFREETE},
          {id: PRODUCT_SUB_TYPE.DR___3POPENSOURCE, name: PRODUCT_SUB_TYPE.DR___3POPENSOURCE},
          {id: PRODUCT_SUB_TYPE.DR___3POEM_ROYALTY, name: PRODUCT_SUB_TYPE.DR___3POEM_ROYALTY},
          {id: PRODUCT_SUB_TYPE.DR___3PTECHDISABLED, name: PRODUCT_SUB_TYPE.DR___3PTECHDISABLED},
          {id: PRODUCT_SUB_TYPE.DR___3PRLTY_STDALONE, name: PRODUCT_SUB_TYPE.DR___3PRLTY_STDALONE},
          {id: PRODUCT_SUB_TYPE.DR___Developed_Record, name: PRODUCT_SUB_TYPE.DR___Developed_Record},
          {id: PRODUCT_SUB_TYPE.DR___Internal_Project, name: PRODUCT_SUB_TYPE.DR___Internal_Project},
          {id: PRODUCT_SUB_TYPE.DR___MTU, name: PRODUCT_SUB_TYPE.DR___MTU},
          {id: PRODUCT_SUB_TYPE.DR___Web_Display_Developed_Rec, name: PRODUCT_SUB_TYPE.DR___Web_Display_Developed_Rec},
          {id: PRODUCT_SUB_TYPE.DM___Developed_Module, name: PRODUCT_SUB_TYPE.DM___Developed_Module},
          {id: PRODUCT_SUB_TYPE.ED___Education, name: PRODUCT_SUB_TYPE.ED___Education},
          {id: PRODUCT_SUB_TYPE.HD___Hardware, name: PRODUCT_SUB_TYPE.HD___Hardware},
          {id: PRODUCT_SUB_TYPE.LA___Development_License, name: PRODUCT_SUB_TYPE.LA___Development_License},
          {id: PRODUCT_SUB_TYPE.LA___Hot_Backup_License, name: PRODUCT_SUB_TYPE.LA___Hot_Backup_License},
          {id: PRODUCT_SUB_TYPE.LA___Licensed_Add_On, name: PRODUCT_SUB_TYPE.LA___Licensed_Add_On},
          {id: PRODUCT_SUB_TYPE.LP___Beta_Product, name: PRODUCT_SUB_TYPE.LP___Beta_Product},
          {id: PRODUCT_SUB_TYPE.LP___Licensed_Product, name: PRODUCT_SUB_TYPE.LP___Licensed_Product},
          {id: PRODUCT_SUB_TYPE.LP___PSEUDO_DEV, name: PRODUCT_SUB_TYPE.LP___PSEUDO_DEV},
          {id: PRODUCT_SUB_TYPE.LP___PSEUDO_MKT, name: PRODUCT_SUB_TYPE.LP___PSEUDO_MKT},
          {id: PRODUCT_SUB_TYPE.LY___Licensed_3rd_Party, name: PRODUCT_SUB_TYPE.LY___Licensed_3rd_Party},
          {id: PRODUCT_SUB_TYPE.PM___Promo_Package, name: PRODUCT_SUB_TYPE.PM___Promo_Package},
          {id: PRODUCT_SUB_TYPE.PP___Integrated_Package, name: PRODUCT_SUB_TYPE.PP___Integrated_Package},
          {id: PRODUCT_SUB_TYPE.PP___Non_Integrated_Package, name: PRODUCT_SUB_TYPE.PP___Non_Integrated_Package},
          {id: PRODUCT_SUB_TYPE.PS___Professional_Services, name: PRODUCT_SUB_TYPE.PS___Professional_Services},
          {id: PRODUCT_SUB_TYPE.SP___Service_Product, name: PRODUCT_SUB_TYPE.SP___Service_Product},
          {id: PRODUCT_SUB_TYPE.RY_Resale_of_3rd_Party_Product, name: PRODUCT_SUB_TYPE.RY_Resale_of_3rd_Party_Product},
          {id: PRODUCT_SUB_TYPE.RO___Membership_Fees, name: PRODUCT_SUB_TYPE.RO___Membership_Fees},
          {id: PRODUCT_SUB_TYPE.RO___Others, name: PRODUCT_SUB_TYPE.RO___Others}
        ],
      } as AdvancedFilterSelectionTypeData,
    }
  ];

  public get tableRowData(): any[] {
    return (this.filteredRowData.length === 0 && !this.filterText && this.selectedAdvancedFilters.length === 0 && Object.keys(this.selectedFeedbackSimpleFilters).length === 0)
      ? this.rowData
      : this.filteredRowData;
  }

  // show or hide table pagination and page report
  public get isShowPagination(): boolean {
    return this.rowData && Array.isArray(this.rowData)
      ? this.tableRowData.length > this.rows
      : false;
  }

  private get componentsColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.VERSION,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get feedbackBySnColumnsConfig(): ColumnConfig[] {
    return [
      {
        ...FEEDBACK_COLUMN_CONFIG.TYPE,
        cellTemplate: this.feedbackTypeTemplate,
        cellTooltip: ((dataCell: DataCellTooltipParams) => {
          return dataCell.dataItem[dataCell.column.field];
        })
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.DATE,
        cellTemplate: this.dateTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.MESSAGE,
        cellTemplate: this.tableCellTemplate,
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.DESCR,
        cellTemplate: this.descrTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.AUTHOR,
        cellTemplate: this.emailTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.LINK,
        cellTemplate: this.pageLinkTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.MACRO_TYPE,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get defaultFeedbackColumnsConfig(): ColumnConfig[] {
    return [
      {
        ...FEEDBACK_COLUMN_CONFIG.TYPE,
        cellTemplate: this.feedbackTypeTemplate,
        cellTooltip: ((dataCell: DataCellTooltipParams) => {
          return dataCell.dataItem[dataCell.column.field];
        })
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.SN,
        cellTemplate: this.smartNumberTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.DATE,
        cellTemplate: this.dateTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.MESSAGE,
        cellTemplate: this.tableCellTemplate,
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.DESCR,
        cellTemplate: this.descrTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.AUTHOR,
        cellTemplate: this.emailTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.LINK,
        cellTemplate: this.pageLinkTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.PRODUCT_NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.VERSION,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.SP_NAME,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get FEEDBACK_COLUMN_MACRO_TYPE(): ColumnConfig {
    return {
      ...FEEDBACK_COLUMN_CONFIG.MACRO_TYPE,
      cellTemplate: this.tableCellTemplate
    };
  }

  private get ADMINISTRATION_ADDITIONAL_FEEDBACK_COLUMNS(): ColumnConfig[] {
    return [
      {
        ...FEEDBACK_COLUMN_CONFIG.SOURCE_OR_MACRO_TYPE,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...FEEDBACK_COLUMN_CONFIG.SOURCE,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get reportProductsColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.PUBLISHING_STATUS,
        headerClass: 'report-table-group-cell-1',
        dataCellClass: 'report-table-group-cell-1',
        cellTemplate: this.publishingStatusCellTemplate,
        frozen: true
      },
      {
        ...COLUMN_CONFIG.PUBLISHED_DATA,
        headerClass: 'report-table-group-cell-1',
        dataCellClass: 'report-table-group-cell-1',
        cellTemplate: this.publishedVersionCellTemplate
      },
      {
        ...COLUMN_CONFIG.TABS,
        headerClass: 'report-table-group-cell-1',
        dataCellClass: 'report-table-group-cell-1',
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.EDITOR,
        headerClass: 'report-table-group-cell-1',
        dataCellClass: 'report-table-group-cell-1',
        cellTemplate: this.emailTemplate
      },
      {
        ...COLUMN_CONFIG.PRODUCT_STATUS,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.productStatusCellTemplate
      },
      {
        ...COLUMN_CONFIG.PRODUCT_TYPE,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.NAME,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '140px'
      },
      {
        ...COLUMN_CONFIG.VERSION,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.SP_FP_PATCH,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.SMART_NUMBER,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.linkCellTemplate
      },
      {
        ...COLUMN_CONFIG.SUB_TYPE,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '140px'
      },
      {
        ...COLUMN_CONFIG.BUSINESS_UNIT,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        headerTooltip: 'BUSINESS UNIT',
        width: '75px'
      },
      {
        ...COLUMN_CONFIG.PRODUCT_LINE,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.PRODUCT_FAMILY,
        headerClass: 'report-table-group-cell-2',
        dataCellClass: 'report-table-group-cell-2',
        cellTemplate: this.tableCellTemplate,
        width: '140px'
      },
      {
        ...COLUMN_CONFIG.RELEASE_DATE,
        headerClass: 'report-table-group-cell-3',
        dataCellClass: 'report-table-group-cell-3',
        cellTemplate: this.dateTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.FULL_SUPP_END_DATE,
        headerClass: 'report-table-group-cell-3',
        dataCellClass: 'report-table-group-cell-3',
        cellTemplate: this.dateTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.END_OF_VERSION_SUPPORT,
        headerClass: 'report-table-group-cell-3',
        dataCellClass: 'report-table-group-cell-3',
        cellTemplate: this.dateTemplate,
        width: '100px'
      },
      {
        ...COLUMN_CONFIG.POSITIVE_FEEDBACK,
        headerClass: 'report-table-group-cell-4',
        dataCellClass: 'report-table-group-cell-4',
        cellTemplate: this.tableCellTemplate,
        headerTitleTemplate: this.thumbUpHeaderTitleTemplate
      },
      {
        ...COLUMN_CONFIG.NEGATIVE_FEEDBACK,
        headerClass: 'report-table-group-cell-4',
        dataCellClass: 'report-table-group-cell-4',
        cellTemplate: this.tableCellTemplate,
        headerTitleTemplate: this.thumbDownHeaderTitleTemplate
      },
      {
        ...COLUMN_CONFIG.VIEWS,
        headerClass: 'report-table-group-cell-4',
        dataCellClass: 'report-table-group-cell-4',
        cellTemplate: this.tableCellTemplate,
      },
      {
        ...COLUMN_CONFIG.VIEWS_EMPTY,
        headerClass: 'report-table-group-cell-4',
        dataCellClass: 'report-table-group-cell-4',
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get productVersionsTableConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.PUBLISHING_STATUS,
        cellTemplate: this.publishingStatusCellTemplate,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.PRODUCT_STATUS,
        cellTemplate: this.productStatusCellTemplate,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.PUBLISHED_DATA,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'cmxa-cell-published-data highlight' : 'cmxa-cell-published-data';
        },
        cellTemplate: this.publishedVersionCellTemplate,
        width: '90px',
      },
      {
        ...COLUMN_CONFIG.PRODUCT_TYPE,
        cellTemplate: this.tableCellTemplate,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.SMART_NUMBER,
        cellTemplate: this.linkCellTemplate,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'disabled' : '';
        }
      },
      {
        ...COLUMN_CONFIG.VERSION,
        cellTemplate: this.tableCellTemplate,
        width: '120px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight bold' : '';
        }
      },
      {
        ...COLUMN_CONFIG.NAME,
        cellTemplate: this.tableCellTemplate,
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight bold' : '';
        }
      },
      {
        ...COLUMN_CONFIG.SP_FP_PATCH,
        cellTemplate: this.tableCellTemplate,
        width: '110px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.SUB_TYPE,
        cellTemplate: this.tableCellTemplate,
        width: '160px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.RELEASE_DATE,
        cellTemplate: this.dateTemplate,
        width: '100px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.FULL_SUPP_END_DATE,
        cellTemplate: this.dateTemplate,
        width: '100px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      },
      {
        ...COLUMN_CONFIG.END_OF_VERSION_SUPPORT,
        cellTemplate: this.dateTemplate,
        width: '100px',
        dataCellClass: (rowData: RowDataItem) => {
          return rowData.dataItem.id === this.currentProduct.id ? 'highlight' : '';
        }
      }
    ];
  }

  private get productsColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.VERSION,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.SP_FP_PATCH,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.SMART_NUMBER,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.SUB_TYPE,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.PRODUCT_TYPE,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.PRODUCT_STATUS,
        cellTemplate: this.productStatusCellTemplate
      },
      {
        ...COLUMN_CONFIG.PUBLISHING_STATUS,
        cellTemplate: this.publishingStatusCellTemplate
      }
    ];
  }

  private get operatingSystemsColumnsConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.OS_NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_VERSION,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_VENDOR,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_ARCHITECTURE,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_FAMILY,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_HW_NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_HW_ARCHITECTURE,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get additionalOperatingSystemColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.OS_NOTES,
        dataCellClass: ((dataCell: DataCellTooltipParams): string => {
          return dataCell.dataItem.osNote && dataCell.dataItem.osNote.noteContent.trim() ? 'icon-cell' : 'icon-cell disabled-icon';
        }),
        cellTemplate: this.osNotesTableCellTemplate
      },
      {
        ...COLUMN_CONFIG.OS_DETAILS,
        dataCellClass: ((dataCell: DataCellTooltipParams): string => {
          const isOsComponents = Object.entries(dataCell.dataItem.osComponents)
            .some(([categoryName, categoryInfo]: [string, (OS_CMP_CATEGORY | Language)[]]): boolean => categoryInfo && categoryInfo.length > 0);
          return isOsComponents ? 'icon-cell' : 'icon-cell disabled-icon';
        }),
        cellTemplate: this.osDetailsTableCellTemplate
      }
    ];
  }

  private get techLanguagesColumnsConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.NAME,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get techTypesColumnsConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.CMP_TYPE_NAME,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get technologiesColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.CMP_VENDOR,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.CMP_NAME,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.CMP_VERSION,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.CMP_ARCHITECTURE,
        cellTemplate: this.tableCellTemplate
      }
    ];
  }

  private get prodInstCmpTypeColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.PROD_INST_CMP_TYPE,
        cellTemplate: this.techTypeTableCellTemplate
      }
    ];
  }

  private get pureCmpTypeColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.CMP_TYPE,
        cellTemplate: this.masterTechTypeTableCellTemplate
      }
    ];
  }

  private get dropdownCmpTypeColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.CMP_TYPE,
        cellTemplate: this.dropdownCmpTypeTableCellTemplate
      }
    ];
  }

  private get dbNotesColumnConfig(): ColumnConfig[] {
    return [
      {
        ...COLUMN_CONFIG.CMP_DB_NOTES,
        dataCellClass: ((dataCell: DataCellTooltipParams): string => {
          return dataCell.dataItem.note && dataCell.dataItem.note.content ? 'icon-cell' : 'icon-cell disabled-icon';
        }),
        cellTemplate: this.dbNotesTableCellTemplate
      }
    ];
  }

  private get productHistoryTableConfig(): ColumnConfig[] {
    return[
      {
        ...COLUMN_CONFIG.HISTORY_USER,
        cellTemplate: this.emailTemplate
      },
      {
        ...COLUMN_CONFIG.HISTORY_ACTIVITY,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.HISTORY_TYPE,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.HISTORY_DESCR,
        cellTemplate: this.tableCellTemplate
      },
      {
        ...COLUMN_CONFIG.HISTORY_DATE,
        cellTemplate: this.dateTimeTemplate
      },
    ];
  }

  private get emailReportColumnConfig(): ColumnConfig[] {
    return [
      {
        ...EMAIL_COLUMN_CONFIG.DATE,
        cellTemplate: this.dateTimeTemplate
      },
      {
        ...EMAIL_COLUMN_CONFIG.LAST_PUBLISHED_DATE,
        cellTemplate: this.dateTimeTemplate
      },
      {
        ...EMAIL_COLUMN_CONFIG.ADDRESSED,
        cellTemplate: this.emailsIsAddressedTemplate,
        cellTooltip: ((dataCell: DataCellTooltipParams) => {
          return dataCell.dataItem[dataCell.column.field];
        })
      },
      {
        ...EMAIL_COLUMN_CONFIG.TITLE
      },
      {
        ...EMAIL_COLUMN_CONFIG.SMART_NUMBER,
        cellTemplate: this.smartNumberTemplate
      },
      {
        ...EMAIL_COLUMN_CONFIG.RECEPIENT
      },
      {
        ...EMAIL_COLUMN_CONFIG.TYPE
      },
      {
        ...EMAIL_COLUMN_CONFIG.DAYS
      },
      {
        ...EMAIL_COLUMN_CONFIG.VIEW,
        cellTemplate: this.viewEmailButtonTemplate
      },
      {
        ...EMAIL_COLUMN_CONFIG.LOG,
        cellTemplate: this.viewLogButtonTemplate
      }
    ];
  }

  constructor(private globalTableFilterService: GlobalTableFilterService,
              private globalAdvancedTableFilterService: GlobalAdvancedTableFilterService,
              private globalSimpleTableFilterService: GlobalSimpleTableFilterService,
              private route: ActivatedRoute,
              private exportService: TableExportService,
              private zone: NgZone,
              private router: Router) {
  }

  public static handleReportDate(tableDateType: TABLE_DATE_TYPE, dates: moment.Moment[] | TableDate[] | TableDateTime[]): moment.Moment[] | TableDate[] {
    const valuesDate: TableDate[] = [];
    const valuesDateTime: moment.Moment[] = [];

    if (tableDateType === TABLE_DATE_TYPE.DATE_TIME) {
      for (const curDate of dates) {
        if (curDate) {
          valuesDate.push(new TableDate(
            moment(curDate).year(),
            moment(curDate).month(),
            moment(curDate).date()
          ));
        } else {
          valuesDate.push(null);
        }
      }
      return valuesDate;
    } else {
      for (const curDate of dates) {
        if (curDate) {
          valuesDateTime.push(
            moment([
              Number(curDate.year),
              Number(curDate.month),
              Number(curDate.date)
            ])
          );
        } else {
          valuesDateTime.push(null);
        }
      }
      return valuesDateTime;
    }
  }

  ngOnInit(): void {
    if (this.isToolbar) {
      this.toolbarConfig.rightCustomSectionTemplate = this.toolbarTableFilterTemplate;
      this.toolbarConfig.visibleColumnsMenu = this.visibleColumnsMenu;
    }

    switch (this.tableType) {
      case TABLE_TYPE.MASTER_TYPES:
        this.columnConfig = this.techTypesColumnsConfig;
        break;
      case TABLE_TYPE.LANGUAGES:
        this.columnConfig = [...this.techLanguagesColumnsConfig, ...this.actionsColumnConfig];
        break;
      case TABLE_TYPE.MASTER_LANGUAGES:
        this.columnConfig = this.techLanguagesColumnsConfig;
        break;
      case TABLE_TYPE.OPERATING_SYSTEMS:
        this.columnConfig = [...this.operatingSystemsColumnsConfig, ...this.additionalOperatingSystemColumnConfig, ...this.actionsColumnConfig];
        break;
      case TABLE_TYPE.MASTER_OPERATING_SYSTEMS:
        this.columnConfig = this.operatingSystemsColumnsConfig;
        break;
      case TABLE_TYPE.TECHNOLOGIES:
        this.columnConfig = this.category === OS_CMP_CATEGORY.DBMS
          ? [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig, ...this.dbNotesColumnConfig, ...this.actionsColumnConfig]
          : [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig, ...this.actionsColumnConfig];
        break;
      case TABLE_TYPE.MASTER_TECHNOLOGIES:
        this.columnConfig = this.category
          ? [...this.technologiesColumnConfig, ...this.dropdownCmpTypeColumnConfig]
          : [...this.technologiesColumnConfig, ...this.pureCmpTypeColumnConfig];
        break;
      case TABLE_TYPE.COMPONENTS:
        this.columnConfig = [...this.componentsColumnConfig, ...this.actionsColumnConfig];
        break;
      case TABLE_TYPE.MASTER_COMPONENTS:
        this.columnConfig = this.componentsColumnConfig;
        break;
      case TABLE_TYPE.PRODUCTS:
        this.columnConfig = [...this.productsColumnConfig, ...this.actionsColumnConfig];
        break;
      case TABLE_TYPE.MASTER_PRODUCTS:
        this.columnConfig = this.productsColumnConfig;
        break;
      case TABLE_TYPE.REPORT_PRODUCTS:
        this.toolbarConfig.filterTemplate = this.advancedFilterTemplate;
        this.toolbarConfig.export = {
          exportTypes: [
            {
              type: ExportType.CSV,
            },
            {
              type: ExportType.PDF
            }
          ],
          showExportForSelected: true
        };
        this.columnConfig = this.reportProductsColumnConfig;
        this.toolbarConfig.visibleColumnsMenuTemplate = this.visibleColumnsCustomTemplate;
        this.toolbarConfig.leftCustomSection = true;
        this.toolbarConfig.leftCustomSectionFixed = true;
        this.toolbarConfig.leftCustomSectionTemplate = this.leftCustomSectionTemplate;
        break;
      case FEEDBACK_TABLE_TYPE.BY_SN_CLIENT:
        this.columnConfig = this.feedbackBySnColumnsConfig;
        this.toolbarConfig.filterTemplate = this.feedbackFilterTemplate;
        this.toolbarConfig.export = {
          exportTypes: [
            {
              type: ExportType.CSV,
            },
            {
              type: ExportType.PDF
            }
          ],
        };
        break;
      case FEEDBACK_TABLE_TYPE.ALL_CLIENT:
        this.columnConfig = this.defaultFeedbackColumnsConfig;
        this.toolbarConfig.filterTemplate = this.feedbackFilterTemplate;
        this.columnConfig.push(this.FEEDBACK_COLUMN_MACRO_TYPE);
        this.toolbarConfig.export = {
          exportTypes: [
            {
              type: ExportType.CSV,
            },
            {
              type: ExportType.PDF
            }
          ],
        };
        break;
      case FEEDBACK_TABLE_TYPE.ALL:
        this.columnConfig = this.defaultFeedbackColumnsConfig;
        this.columnConfig = [...this.columnConfig, ...this.ADMINISTRATION_ADDITIONAL_FEEDBACK_COLUMNS];
        this.toolbarConfig.filterTemplate = this.feedbackFilterTemplate;
        this.toolbarConfig.export = {
          exportTypes: [
            {
              type: ExportType.CSV,
            },
            {
              type: ExportType.PDF
            }
          ],
        };
        break;
      case TABLE_TYPE.PRODUCT_VERSIONS:
        this.columnConfig = this.productVersionsTableConfig;
        break;
      case TABLE_TYPE.HISTORY:
        this.columnConfig = this.productHistoryTableConfig;
        break;
      case TABLE_TYPE.EMAIL_REPORT:
        this.columnConfig = this.emailReportColumnConfig;
        this.toolbarConfig.filterTemplate = this.emailManagementFilterTemplate;
        this.toolbarConfig.export = {
          exportTypes: [
            {
              type: ExportType.CSV,
            },
            {
              type: ExportType.PDF
            }
          ],
        };
    }
  }

  ngOnChanges(changes: MainPrimeNgTableComponentChanges): void {
    if (changes.rowData && Array.isArray(changes.rowData.currentValue)) {
      this.loading = false;
      if (this.tableType === TABLE_TYPE.REPORT_PRODUCTS) {
        const visibilityColumnsConfig: UserPreference = this.userPreferences
          .find((curUserPreference: UserPreference): boolean => curUserPreference.userPreferenceKey === USER_PREFERENCE_KEY.UPK_REPORT_VISIBLE_COLUMNS);

        if (visibilityColumnsConfig) {
          this.updateTableVisibilityColumns(visibilityColumnsConfig.value);
        }

        this.advancedFilterOptions = this.advancedFilterOptions.concat(this.addAdditionalAdvancedFilterColumns(this.rowData));
        this.updateTableFilterConfiguration(this.tableFilterUrlParams);
      } else if (this.tableType === TABLE_TYPE.PRODUCTS && (this.filteredRowData || this.filterText)) {
        this.filteredRowData = [];
      }
    }
  }

  public onFilterTable(text: string): void {
    if (text === '' && this.selectedAdvancedFilters.length === 0 && !this.selectedFeedbackSimpleFilters) {
      this.filteredRowData = [];
    } else if (this.filterText.length < text.length && this.filteredRowData.length !== 0) {
      switch (this.tableType) {
        case TABLE_TYPE.MASTER_TYPES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<CmpType>(this.filteredRowData, text, this.columnConfig) as CmpType[];
          break;
        case TABLE_TYPE.LANGUAGES:
        case TABLE_TYPE.MASTER_LANGUAGES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<Language>(this.filteredRowData, text, this.columnConfig) as Language[];
          break;
        case TABLE_TYPE.OPERATING_SYSTEMS:
          this.filteredRowData = this.globalTableFilterService
            .filterTableRows<OperatingSystem>(this.filteredRowData, text, this.operatingSystemsColumnsConfig) as OperatingSystem[];
          break;
        case TABLE_TYPE.MASTER_OPERATING_SYSTEMS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OperatingSystem>(this.filteredRowData, text, this.columnConfig) as OperatingSystem[];
          break;
        case TABLE_TYPE.TECHNOLOGIES:
          const columns: ColumnConfig[] = this.category === OS_CMP_CATEGORY.DBMS
            ? [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig]
            : [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig];
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OsComponent>(this.filteredRowData, text, columns) as OsComponent[];
          break;
        case TABLE_TYPE.MASTER_TECHNOLOGIES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OsComponent>(this.filteredRowData, text, this.columnConfig) as OsComponent[];
          break;
        case TABLE_TYPE.COMPONENTS:
        case TABLE_TYPE.MASTER_COMPONENTS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductInfo>(this.filteredRowData, text, this.columnConfig) as ProductInfo[];
          break;
        case TABLE_TYPE.PRODUCTS:
        case TABLE_TYPE.MASTER_PRODUCTS:
        case TABLE_TYPE.PRODUCT_VERSIONS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductInfo>(this.filteredRowData, text, this.columnConfig) as ProductInfo[];
          break;
        case TABLE_TYPE.REPORT_PRODUCTS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ReportProduct>(this.filteredRowData, text, this.columnConfig) as ReportProduct[];
          this.filteredRowData = this.globalAdvancedTableFilterService.filter(this.filteredRowData, this.selectedAdvancedFilters);
          break;
        case FEEDBACK_TABLE_TYPE.BY_SN_CLIENT:
        case FEEDBACK_TABLE_TYPE.ALL_CLIENT:
        case FEEDBACK_TABLE_TYPE.ALL:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<FeedbackModel>(this.filteredRowData, text, this.columnConfig) as FeedbackModel[];
          this.filteredRowData = this.globalSimpleTableFilterService
              .filter<FeedbackModel[], SimpleFilterKey2FilterOptions>(this.filteredRowData, this.selectedFeedbackSimpleFilters);
          break;
        case TABLE_TYPE.HISTORY:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductHistory>(this.filteredRowData, text, this.columnConfig) as ProductHistory[];
          break;
        case TABLE_TYPE.EMAIL_REPORT:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<EmailModel>(this.filteredRowData, text, this.columnConfig) as EmailModel[];
          this.filteredRowData = this.globalSimpleTableFilterService
              .filter<EmailModel[], SimpleFilterKey2FilterOptions>(this.filteredRowData, this.selectedFeedbackSimpleFilters);
          break;
      }
    } else {
      switch (this.tableType) {
        case TABLE_TYPE.MASTER_TYPES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<CmpType>(this.rowData, text, this.columnConfig) as CmpType[];
          break;
        case TABLE_TYPE.LANGUAGES:
        case TABLE_TYPE.MASTER_LANGUAGES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<Language>(this.rowData, text, this.columnConfig) as Language[];
          break;
        case TABLE_TYPE.OPERATING_SYSTEMS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OperatingSystem>(this.rowData, text, this.operatingSystemsColumnsConfig) as OperatingSystem[];
          break;
        case TABLE_TYPE.MASTER_OPERATING_SYSTEMS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OperatingSystem>(this.rowData, text, this.columnConfig) as OperatingSystem[];
          break;
        case TABLE_TYPE.TECHNOLOGIES:
          const columns: ColumnConfig[] = this.category === OS_CMP_CATEGORY.DBMS
            ? [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig]
            : [...this.technologiesColumnConfig, ...this.prodInstCmpTypeColumnConfig];
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OsComponent>(this.rowData, text, columns) as OsComponent[];
          break;
        case TABLE_TYPE.MASTER_TECHNOLOGIES:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<OsComponent>(this.rowData, text, this.columnConfig) as OsComponent[];
          break;
        case TABLE_TYPE.COMPONENTS:
        case TABLE_TYPE.MASTER_COMPONENTS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductInfo>(this.rowData, text, this.columnConfig) as ProductInfo[];
          break;
        case TABLE_TYPE.PRODUCTS:
        case TABLE_TYPE.MASTER_PRODUCTS:
        case TABLE_TYPE.PRODUCT_VERSIONS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductInfo>(this.rowData, text, this.columnConfig) as ProductInfo[];
          break;
        case TABLE_TYPE.REPORT_PRODUCTS:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ReportProduct>(this.rowData, text, this.columnConfig) as ReportProduct[];
          this.filteredRowData = this.globalAdvancedTableFilterService.filter(this.filteredRowData, this.selectedAdvancedFilters);
          break;
        case FEEDBACK_TABLE_TYPE.BY_SN_CLIENT:
        case FEEDBACK_TABLE_TYPE.ALL_CLIENT:
        case FEEDBACK_TABLE_TYPE.ALL:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<FeedbackModel>(this.rowData, text, this.columnConfig) as FeedbackModel[];
          this.filteredRowData = this.globalSimpleTableFilterService
              .filter<FeedbackModel[], SimpleFilterKey2FilterOptions>(this.filteredRowData, this.selectedFeedbackSimpleFilters);
          break;
        case TABLE_TYPE.HISTORY:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<ProductHistory>(this.rowData, text, this.columnConfig) as ProductHistory[];
          break;
        case TABLE_TYPE.EMAIL_REPORT:
          this.filteredRowData = this.globalTableFilterService.filterTableRows<EmailModel>(this.rowData, text, this.columnConfig) as EmailModel[];
          this.filteredRowData = this.globalSimpleTableFilterService
              .filter<EmailModel[], SimpleFilterKey2FilterOptions>(this.filteredRowData, this.selectedFeedbackSimpleFilters);
          break;
      }
    }

    if (this.tableType === TABLE_TYPE.REPORT_PRODUCTS) {
      this.updateUrlParams(text, this.selectedAdvancedFilters);
    }
  }

  public onReleaseDatePresetChange(pastDaysPresetModel: number, filterControlsComponent: AdaptFilterControlsComponent): void {
    switch (this.pastDaysPresetModel) {
      case 90:
        filterControlsComponent.onFilterOptionModelChange(this.createFilterOptionsWithPastDays(90));
        break;
      case 180:
        filterControlsComponent.onFilterOptionModelChange(this.createFilterOptionsWithPastDays(180));
        break;
    }
  }

  public onDateTimeTypeFilterOptionModelChange(model: moment.Moment, filterControlsComponent: AdaptFilterControlsComponent): void {
    this.pastDaysPresetModel = null;
    filterControlsComponent.onFilterOptionModelChange(model);
  }

  // action for preset filter buttons on the toolbar
  public activateSharedAdvancedFilter(sharedFilter: TableSavedAdvancedFilter): void {
    this.pastDaysPresetModel = sharedFilter.pastDays;
    this.selectedAdvancedFilters = sharedFilter.filters;
    this.activeSavedFilter = sharedFilter;
    this.onSelectedAdvancedFiltersChange(this.selectedAdvancedFilters);
  }

  // handle events for buttons "apply" and "remove all" in the advanced filter
  onSelectedAdvancedFiltersChange(selectedFilters: SelectedAdvancedFilter[]): void {
    this.selectedAdvancedFilters = selectedFilters;
    const isAvailabilityDateFilter = selectedFilters.some((curFilter: SelectedAdvancedFilter): boolean => curFilter.filterOptionId === 'availabityDate');

    if (!isAvailabilityDateFilter) {
      this.pastDaysPresetModel = null;
    }

    if (selectedFilters.length === 0 && !this.filterText) {
      this.filteredRowData = [];
      this.selectedAdvancedFilters = [];
      this.activeSavedFilter = null;
    } else if (selectedFilters.length === 0 && this.filterText) {
      this.filteredRowData = this.globalTableFilterService.filterTableRows<ReportProduct>(this.rowData, this.filterText, this.columnConfig) as ReportProduct[];
    } else if (selectedFilters.length > 0 && !this.filterText) {
      this.filteredRowData = this.globalAdvancedTableFilterService.filter(this.rowData, this.selectedAdvancedFilters);
    } else {
      this.filteredRowData = this.globalTableFilterService.filterTableRows<ReportProduct>(this.rowData, this.filterText, this.columnConfig) as ReportProduct[];
      this.filteredRowData = this.globalAdvancedTableFilterService.filter(this.filteredRowData, this.selectedAdvancedFilters);
    }

    this.updateUrlParams(this.filterText, this.selectedAdvancedFilters);
  }

  // handle events for buttons "clear" and "remove all" in the advanced filter
  public onFiltersCleared(selectedFilters: SelectedAdvancedFilter[]): void {
    this.onSelectedAdvancedFiltersChange(selectedFilters);
    this.activeSavedFilter = null;
    this.pastDaysPresetModel = null;
  }

  public onActiveSavedFilterChange(savedAdvancedFilter: SavedAdvancedFilter): void {
    this.handleSavedAdvancedFilter.emit(new SavedAdvancedFilterEvent(
      savedAdvancedFilter,
      this.savedFilters
    ));
  }

  public onCreateNewAdvancedFilter(createAdvancedFilterEvent: CreateAdvancedFilterEvent): void {
    const savedNewAdvancedFilter: TableSavedAdvancedFilter = {
      id: this.savedFilters.length.toString(),
      name: createAdvancedFilterEvent.name,
      filters: createAdvancedFilterEvent.filters,
      pastDays: this.pastDaysPresetModel
    };

    this.savedFilters.push(savedNewAdvancedFilter);
    this.activeSavedFilter = savedNewAdvancedFilter;
    this.onSelectedAdvancedFiltersChange(savedNewAdvancedFilter.filters);

    this.handleSavedAdvancedFilter.emit(new SavedAdvancedFilterEvent(
      this.activeSavedFilter,
      this.savedFilters
    ));
  }

  public onUpdateSavedAdvancedFilter(savedAdvancedFilterEvent: UpdateSavedFilterEvent): void {
    const savedAdvancedFilter = savedAdvancedFilterEvent.newSavedFilter;

    this.savedFilters = this.savedFilters.map((curFilter: SavedAdvancedFilter): SavedAdvancedFilter => {
      if (curFilter.id === savedAdvancedFilter.id) {
        return {
          ...curFilter,
          name: savedAdvancedFilter.name,
          filters: savedAdvancedFilter.filters
        };
      }

      return curFilter;
    });
  }

  public onDeleteSavedAdvancedFilter(savedAdvancedFilter: SavedAdvancedFilter): void {
    const index: number = this.savedFilters.findIndex((curFilter: SavedAdvancedFilter): boolean => curFilter.id === savedAdvancedFilter.id);
    let isRemoveCurrentActiveSavedConfig = false;

    // need to clear all filters when the user remove active saved filter
    if (this.activeSavedFilter && savedAdvancedFilter.id === this.activeSavedFilter.id) {
      this.selectedAdvancedFilters = [];
      this.activeSavedFilter = null;
      isRemoveCurrentActiveSavedConfig = true;
      this.updateUrlParams(this.filterText, this.selectedAdvancedFilters);
      this.onSelectedAdvancedFiltersChange(this.selectedAdvancedFilters);
    }

    if (index >= 0) {
      this.savedFilters.splice(index, 1);
    }

    this.savedFilters = this.savedFilters.map((curFilter: SavedAdvancedFilter): SavedAdvancedFilter => ({
      ...curFilter,
      id: curFilter.id.toString()
    }));

    if (!isRemoveCurrentActiveSavedConfig) {
      this.handleSavedAdvancedFilter.emit(new SavedAdvancedFilterEvent(
        this.activeSavedFilter,
        this.savedFilters
      ));
    }
  }

  public onBeforeActiveSavedFilterChange(beforeActiveSavedFilterChangeEvent: BeforeActiveSavedFilterChangeEvent): void {
    if (!beforeActiveSavedFilterChangeEvent.savedFilterToSelect) {
      this.pastDaysPresetModel = null;
    }

    if (beforeActiveSavedFilterChangeEvent.savedFilterToSelect &&
      beforeActiveSavedFilterChangeEvent.savedFilterToSelect.filters &&
      beforeActiveSavedFilterChangeEvent.savedFilterToSelect.filters.length > 0) {

      if (beforeActiveSavedFilterChangeEvent.savedFilterToSelect.isCreatedByOtherUsers) {
        const presetFilter: TableSavedAdvancedFilter = beforeActiveSavedFilterChangeEvent.savedFilterToSelect as TableSavedAdvancedFilter;
        this.pastDaysPresetModel = presetFilter.pastDays;
      }

      this.onSelectedAdvancedFiltersChange(beforeActiveSavedFilterChangeEvent.savedFilterToSelect.filters);
    }
  }

  public onSelectionChange(selections): void {
    this.selectionChange.emit(selections);
  }

  public onViewEmail(event: EmailModel): void {
    this.viewEmail.emit(event);
  }

  public onViewHistoryLog(event: EmailModel): void {
    this.viewHistoryLog.emit(event);
  }

  public changeCmpTypeDropdown(selectedCmpType: CmpType, osComponent: OsComponent): void {
    osComponent.productInstComponentType = selectedCmpType;
  }

  public onExportAs(exportEvent: ExportEvent): void {
    const visibleColumns: ColumnConfig[] = this.adaptTable.getVisibleColumns();
    const records = exportEvent.selectionOnly ? this.adaptTable.selection : this.tableRowData;
    let fileName = 'BMC_Helix_Report_Products';
    switch (this.tableType) {
      case FEEDBACK_TABLE_TYPE.BY_SN_CLIENT:
      case FEEDBACK_TABLE_TYPE.ALL_CLIENT:
      case FEEDBACK_TABLE_TYPE.ALL:
        fileName = 'CMX_Feedback';
        break;
      case TABLE_TYPE.EMAIL_REPORT:
        fileName = 'CMXA Email Report';
        break;
    }

    if (exportEvent.type === ExportType.CSV) {
      this.exportService.handleExportCSV(visibleColumns, records, fileName);
    } else if (exportEvent.type === ExportType.PDF) {
      // Use Pdf type instead of Excel. As Adapt has no possibility to export Excel
      this.exportService.handleExportExcel(visibleColumns, records, fileName);
    }
  }

  public isLastVisibleColumn(currentColumn: ColumnConfig): boolean {
    const visibleColumns = this.adaptTable.getVisibleColumns();

    return visibleColumns.length === 1 && visibleColumns[0].field === currentColumn.field;
  }

  public onChangeColumnVisibility(event: boolean, column: ColumnConfig): void {
    column.hidden = !event;
    this.adaptTable.columnsVisibilityChange.emit([{column, isHidden: !event}]);
    const nameColumns: string = this.adaptTable.getVisibleColumns().reduce((acc: string, curColumn: ColumnConfig): string => {
      return acc + curColumn.header + ',';
    }, '');

    this.handleVisibilityColumns.emit(nameColumns);
  }

  public activeFeedbackFilter(activeFilterOption: SimpleFilterOption, simpleFilterKey: FEEDBACK_SIMPLE_FILTER_KEY | EMAIL_SIMPLE_FILTER_KEY): void {
    let rowsInfo = this.rowData;

    if (!activeFilterOption) {
      delete this.selectedFeedbackSimpleFilters[simpleFilterKey];
    } else {
      this.selectedFeedbackSimpleFilters[simpleFilterKey] = activeFilterOption;
    }

    if (this.filterText) {
      rowsInfo = this.globalTableFilterService.filterTableRows<FeedbackModel | EmailModel>(this.rowData, this.filterText, this.columnConfig) as any[];
    }

    this.filteredRowData = this.globalSimpleTableFilterService.filter<any[], SimpleFilterKey2FilterOptions>(rowsInfo, this.selectedFeedbackSimpleFilters);
  }

  private addAdditionalAdvancedFilterColumns<T>(rowData: T[]): AdvancedFilterOption[] {
    const baseBusinessUnitSelectOptions: RxSelectOption[] = this.createSelectedOptionsData<T>('baseBusinessUnit', rowData);
    const baseProductLineSelectOptions: RxSelectOption[] = this.createSelectedOptionsData<T>('baseProductLine', rowData);
    const baseProductFamilySelectOptions: RxSelectOption[] = this.createSelectedOptionsData<T>('baseProductFamily', rowData);

    const additionalFilterOptions: AdvancedFilterOption[] = [
      {
        id: 'baseBusinessUnit',
        label: COLUMN_HEADER.BUSINESS_UNIT,
        dataType: AdvancedFilterOptionDataType.selection,
        data: {
          selectOptions: baseBusinessUnitSelectOptions
        } as AdvancedFilterSelectionTypeData,
      },
      {
        id: 'baseProductLine',
        label: COLUMN_HEADER.PRODUCT_LINE,
        dataType: AdvancedFilterOptionDataType.selection,
        data: {
          selectOptions: baseProductLineSelectOptions,
        } as AdvancedFilterSelectionTypeData,
      },
      {
        id: 'baseProductFamily',
        label: COLUMN_HEADER.PRODUCT_FAMILY,
        dataType: AdvancedFilterOptionDataType.selection,
        data: {
          selectOptions: baseProductFamilySelectOptions,
        } as AdvancedFilterSelectionTypeData,
      },
      {
        id: 'availabityDate',
        label: COLUMN_HEADER.RELEASE_DATE,
        dataType: AdvancedFilterOptionDataType.custom,
        inputsTemplate: this.inputsForReleaseDateTemplate,
        customConfig: {

          // Used to take model value and format to user friendly format
          // Example: "2021-01-11T00:00:00.000Z" -> "Jan 11, 2021"
          formatValueForTag: (dates: moment.Moment[]): string => {
            switch (this.pastDaysPresetModel) {
              case 90:
                return 'Past 90 days';
              case 180:
                return 'Past 180 days';
              default:
                return dates.reduce((acc: string, curDate: moment.Moment, index: number): string => {
                  if (curDate) {
                    return index === 1 && acc
                      ? acc + ' - ' + curDate.format('ll')
                      : acc + curDate.format('ll');
                  }
                  return acc;
                }, '');
            }
          }
        }
      }
    ];

    return additionalFilterOptions;
  }

  private createSelectedOptionsData<T>(fieldName: string, rowData: T[]): RxSelectOption[] {
    const rxSelectOptionIntersectMap = new Map();

    for (const curRow of rowData) {
      if (curRow[fieldName]) {
        rxSelectOptionIntersectMap.set(
          curRow[fieldName],
          {
            id: curRow[fieldName],
            name: curRow[fieldName]
          });
      }
    }

    return Array.from(rxSelectOptionIntersectMap.values());
  }

  private updateUrlParams(filterText: string, selectedFilters: SelectedAdvancedFilter[]): void {
    const urlParams: { [key: string]: string } = {};

    if (filterText && filterText !== '') {
      urlParams.filterText = filterText;
    }

    if (selectedFilters && Array.isArray(selectedFilters)) {
      selectedFilters.forEach((curFilter: SelectedAdvancedFilter): void => {
        if (curFilter.value && Array.isArray(curFilter.value) && curFilter.value.length > 0) {
          if (curFilter.filterOptionId !== 'availabityDate') {
            urlParams[curFilter.filterOptionId] = curFilter.value.join(',');
          } else {
            if (this.pastDaysPresetModel) {
              urlParams.pastDays = this.pastDaysPresetModel.toString();
            } else {
              urlParams[curFilter.filterOptionId] = JSON.stringify(MainPrimeNgTableComponent.handleReportDate(TABLE_DATE_TYPE.DATE_TIME, curFilter.value));
            }
          }
        } else if (curFilter.filterOptionId === 'hasPublishedVersion') {
          urlParams[curFilter.filterOptionId] = curFilter.value;
        }
      });
    }

    this.zone.run(() => {
      filterText === '' && this.selectedAdvancedFilters.length === 0
        ? this.router.navigate([], {
          relativeTo: this.route,
          queryParams: null,
          skipLocationChange: false
        })
        : this.router.navigate([], {
          relativeTo: this.route,
          queryParams: urlParams
        });
    });
  }

  private updateTableVisibilityColumns(columnNames: string): void {
    if (columnNames) {
      this.columnConfig.forEach((curColumn: ColumnConfig): void => {
        curColumn.hidden = !columnNames.includes(curColumn.header);
      });
    }
  }

  private updateTableFilterConfiguration(tableFilterUrlParams: TableFilterUrlParams): void {
    let isUsedFilterTextUrlParams = false;
    if (this.columnConfig && this.columnConfig.length < 1) {
      this.columnConfig = this.reportProductsColumnConfig;
    }

    for (const urlParam of Object.keys(tableFilterUrlParams)) {
      if (urlParam !== 'filterText' && tableFilterUrlParams[urlParam]) {
        isUsedFilterTextUrlParams = true;
        break;
      }
    }

    if (this.userPreferences) {
      const userPreferenceAdvancedFilter = this.userPreferences
        .find((curUserPreference: UserPreference): boolean => curUserPreference.userPreferenceKey === USER_PREFERENCE_KEY.UPK_REPORT_ADVANCED_FILTER);

      if (userPreferenceAdvancedFilter) {
        const parsedUserPreferenceAdvancedFilter = JSON.parse(userPreferenceAdvancedFilter.value);
        const updatedSavedFilters: TableSavedAdvancedFilter[] = parsedUserPreferenceAdvancedFilter.savedFilters
          .map((curSavedFilter: TableSavedAdvancedFilter): TableSavedAdvancedFilter => {
            curSavedFilter.filters = curSavedFilter.filters.map((curFilter: SelectedAdvancedFilter): SelectedAdvancedFilter => {
              if (curFilter.filterOptionId === 'availabityDate') {
                curFilter.value = curSavedFilter.pastDays
                  ? this.createFilterOptionsWithPastDays(curSavedFilter.pastDays)
                  : MainPrimeNgTableComponent.handleReportDate(TABLE_DATE_TYPE.DATE, curFilter.value);
              }

              return curFilter;
            });

            return curSavedFilter;
          });

        this.savedFilters = [...updatedSavedFilters];

        if (isUsedFilterTextUrlParams) {
          this.activeSavedFilter = null;
        } else {
          this.activeSavedFilter = this.savedFilters.find((curFilter: TableSavedAdvancedFilter): boolean => {
            return parsedUserPreferenceAdvancedFilter.activeSavedFilter && curFilter.id === parsedUserPreferenceAdvancedFilter.activeSavedFilter?.id;
          });
        }

        if (isUsedFilterTextUrlParams && parsedUserPreferenceAdvancedFilter.activeSavedFilter) {
          this.handleSavedAdvancedFilter.emit(new SavedAdvancedFilterEvent(
            this.activeSavedFilter,
            this.savedFilters
          ));
        }
      }
    }

    this.sharedSavedAdvancedFilters = this.createSharedSavedAdvancedFilters(this.advancedFilterOptions);
    this.savedFilters = [...this.savedFilters, ...this.sharedSavedAdvancedFilters];

    if (this.activeSavedFilter) {
      this.selectedAdvancedFilters = this.activeSavedFilter.filters;
      this.pastDaysPresetModel = Number(this.activeSavedFilter.pastDays);
      this.updateUrlParams(this.filterText, this.selectedAdvancedFilters);
      this.onSelectedAdvancedFiltersChange(this.selectedAdvancedFilters);
    }

    const tempSelectedAdvancedFilters: SelectedAdvancedFilter[] = [];
    if (!this.activeSavedFilter) {
      for (const urlParam of Object.keys(tableFilterUrlParams)) {
        switch (urlParam) {
          case 'filterText':
            if (tableFilterUrlParams[urlParam]) {
              this.filterText = tableFilterUrlParams[urlParam];
              this.onFilterTable(this.filterText);
            }
            break;
          case 'publishingStatus':
          case 'productStatus':
          case 'productSubType':
          case 'productFillInTabsCount':
          case 'baseBusinessUnit':
          case 'baseProductLine':
          case 'baseProductFamily':
            if (tableFilterUrlParams[urlParam]) {
              tempSelectedAdvancedFilters.push({
                filterOptionId: urlParam,
                value: tableFilterUrlParams[urlParam].split(',')
              });
            }
            break;
          case 'hasPublishedVersion':
            if (tableFilterUrlParams[urlParam] !== null) {
              tempSelectedAdvancedFilters.push({
                filterOptionId: urlParam,
                value: String(tableFilterUrlParams[urlParam]) === 'true'
              });
            }
            break;
          case 'availabityDate':
            if (tableFilterUrlParams[urlParam] && !tableFilterUrlParams.pastDays) {
              tempSelectedAdvancedFilters.push({
                filterOptionId: urlParam,
                value: MainPrimeNgTableComponent.handleReportDate(TABLE_DATE_TYPE.DATE, JSON.parse(tableFilterUrlParams[urlParam]))
              });
            }
            break;
          case 'pastDays':
            if (tableFilterUrlParams[urlParam]) {
              this.pastDaysPresetModel = Number(tableFilterUrlParams[urlParam]);

              tempSelectedAdvancedFilters.push({
                filterOptionId: 'availabityDate',
                value: this.createFilterOptionsWithPastDays(this.pastDaysPresetModel)
              });
            }
            break;
        }
        this.selectedAdvancedFilters = tempSelectedAdvancedFilters;
      }

      if (this.selectedAdvancedFilters && this.selectedAdvancedFilters.length > 0) {
        this.onSelectedAdvancedFiltersChange(this.selectedAdvancedFilters);
      }
    }
  }

  private createFilterOptionsWithPastDays(pastDays: number | string): FilterValue {
    const now = moment();
    const modelProvider = new AdaptRxDatetimeMomentAdapter();

    if (!pastDays) {
      return [null, null];
    }

    return [modelProvider.toModel(modelProvider.fromModel(now.subtract(Number(pastDays), 'day').startOf('day'))), null];
  }

  private createSharedSavedAdvancedFilters(advancedFilterOptions: AdvancedFilterOption[]): TableSavedAdvancedFilter[] {
    const baseBusinessUnitFilter: AdvancedFilterOption = advancedFilterOptions
      .find((curAdvancedFilterOption: AdvancedFilterOption): boolean => curAdvancedFilterOption.id === 'baseBusinessUnit');
    const sharedSavedAdvancedFilters: TableSavedAdvancedFilter[] = [];

    baseBusinessUnitFilter.data.selectOptions.forEach((curRxSelectOption: RxSelectOption): void => {
      if (curRxSelectOption.id !== '-') {
        const sharedFilter = {
                                id: 'sharedFilter' + (sharedSavedAdvancedFilters.length + 1),
                                name: curRxSelectOption.name,
                                isCreatedByOtherUsers: true,
                                pastDays: 90,
                                filters: [
                                {
                                  filterOptionId: 'publishingStatus',
                                  value: ['NEW']
                                },
                                {
                                  filterOptionId: 'productStatus',
                                  value: ['AVAILABLE']
                                },
                                {
                                  filterOptionId: 'baseBusinessUnit',
                                  value: [curRxSelectOption.name]
                                },
                                {
                                  filterOptionId: 'availabityDate',
                                  value: this.createFilterOptionsWithPastDays(90)
                                }
                              ]
                            };
        sharedSavedAdvancedFilters.push(sharedFilter);
      }
    });

    return sharedSavedAdvancedFilters;
  }
}
